import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { Switch } from '@headlessui/react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { documentService } from '../../../services/documentService';
import { DocumentSharingDto } from '../../../types/documents';
import { useToast } from '../../UI/ToastProvider';

export interface ShareDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string | null;
  documentTitle: string;
  canEditSharing: boolean;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  isOpen,
  onClose,
  documentId,
  documentTitle,
  canEditSharing,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sharing, setSharing] = useState<DocumentSharingDto | null>(null);
  const [toggleSubmitting, setToggleSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = useCallback(() => {
    setSharing(null);
    setLoadError(null);
    setLoading(false);
    setToggleSubmitting(false);
    setCopied(false);
  }, []);

  useEffect(() => {
    if (!isOpen || !documentId) {
      if (!isOpen) reset();
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSharing(null);
    void documentService
      .getDocumentSharing(documentId)
      .then((data) => {
        if (!cancelled) setSharing(data);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const ax = error as { response?: { status?: number; data?: { message?: string } } };
        if (ax.response?.status === 404) {
          setLoadError('Documento não encontrado ou sem acesso.');
        } else {
          setLoadError('Não foi possível carregar o compartilhamento.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, documentId, reset]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const handleTogglePublic = async (next: boolean) => {
    if (!documentId || !canEditSharing || toggleSubmitting) return;
    setToggleSubmitting(true);
    try {
      const updated = await documentService.updateDocumentSharing(documentId, next);
      setSharing(updated);
      addToast(next ? 'Documento público: o link pode ser acessado sem login.' : 'Documento privado.', 'success');
    } catch (error: unknown) {
      const ax = error as { response?: { status?: number; data?: { message?: string } } };
      const msg = ax.response?.data?.message;
      if (ax.response?.status === 403) {
        addToast(msg || 'Acesso negado.', 'error');
      } else if (ax.response?.status === 400) {
        addToast(msg || 'Pastas não podem ser tornadas públicas.', 'error');
      } else if (msg) {
        addToast(msg, 'error');
      } else {
        addToast('Não foi possível atualizar o compartilhamento.', 'error');
      }
    } finally {
      setToggleSubmitting(false);
    }
  };

  const handleCopy = async () => {
    const url = sharing?.canonicalUrl?.trim();
    if (!url) {
      addToast('Link indisponível.', 'error');
      return;
    }
    const ok = await copyTextToClipboard(url);
    if (ok) {
      setCopied(true);
    } else {
      addToast('Não foi possível copiar o link.', 'error');
    }
  };

  const isPublic = sharing?.isPublic ?? false;

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

        <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="mb-1 text-lg font-medium text-gray-900">Compartilhar</Dialog.Title>
          <p className="mb-4 text-sm text-gray-600">
            Link de acesso para <span className="font-medium text-gray-800">{documentTitle}</span>.
            {isPublic ? (
              <span className="block mt-1 text-gray-700">
                Com o documento público, quem tiver o link pode visualizar o conteúdo sem autenticação.
              </span>
            ) : (
              <span className="block mt-1 text-gray-700">
                Com o documento privado, o acesso segue as permissões do módulo; o link pode ser usado por quem já tem
                permissão na aplicação.
              </span>
            )}
          </p>

          {loading && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600" />
              Carregando…
            </div>
          )}

          {!loading && loadError && (
            <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{loadError}</p>
          )}

          {!loading && sharing && (
            <>
              <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-gray-200 bg-gray-50/80 px-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800">Documento público</p>
                  <p className="text-xs text-gray-500">
                    {canEditSharing
                      ? 'Permite acesso anônimo pelo link abaixo.'
                      : 'Apenas quem pode editar o documento altera esta opção.'}
                  </p>
                </div>
                <Switch
                  checked={isPublic}
                  onChange={(v) => void handleTogglePublic(v)}
                  disabled={!canEditSharing || toggleSubmitting}
                  className={`${
                    isPublic ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <span
                    className={`${
                      isPublic ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>

              <div className="mb-2">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Link
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                  <input
                    type="text"
                    readOnly
                    value={sharing.canonicalUrl || ''}
                    className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    type="button"
                    onClick={() => void handleCopy()}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5 shrink-0" aria-hidden />
                    {copied ? 'Copiado' : 'Copiar link'}
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
